/**
 * Apply an assistant-proposed `PlotSpec` to the live plot.
 *
 * Rather than poke plot atoms directly, we serialize the spec into the same
 * query-string wire format the rest of the app uses and hand it to
 * `restoreFromUrl` - which loads the ship's columns, prunes picks to the real
 * schema, sets dates/filters/plot-state, and switches to the Plot tab. Then we
 * fetch + render. This reuses all the existing validation/pruning and keeps the
 * assistant honest: anything it proposes is exactly a shareable plot URL.
 */

import { TABLE_PAGE_SIZE_DEFAULT, serializeTable } from '../plot/table-config.ts';
import { restoreFromUrl, serializeAxisTitle } from '../../state/url-sync.ts';
import { activeSection, activeTab } from '../../state/store.ts';
import {
  assistantDrawerOpen,
  closeAssistantDrawer,
  syncAssistantSurface,
} from '../../state/assistant-surface.ts';
import { doPlot } from '../plot/plot-actions.ts';
import { resizePlot } from '../plot/render/chart-dom.ts';
import {
  startDate,
  endDate,
  filters,
  filterLogic,
  filterExpr,
  filterExprError,
  filterMode,
  outliers,
  derivedSignals,
  DEFAULT_OUTLIERS,
  activeDataSource,
} from '../../state/store.ts';
import { plotState, seededPlotState } from '../../state/plot-state.ts';
import type { PlotSpec, PlotSpecAxisTitle } from '../../api/types.ts';
import {
  normalizeUtcDatetime,
  PERIOD_COMPARISON_VERSION,
  serializePeriodComparison,
} from '../../state/period-comparison.ts';
import { toDatetimeLocal } from '../../utils/dates.ts';
import { assertValidProposalDates } from './proposal-dates.ts';
import { ensureProposalSource } from './proposal-source.ts';
import { serializeNumber } from '../plot/number-config.ts';
import { dialFromSpec, serializeGauge } from '../plot/gauge-config.ts';
import { SANKEY_DISPLAY_DEFAULT, nodesFromSpec, serializeSankey } from '../plot/sankey-config.ts';

/** A proposed Y axis title in the `yt`/`yrt` wire format; empty for auto. */
function specAxisTitle(t: PlotSpecAxisTitle | null | undefined): string {
  if (!t) return '';
  return serializeAxisTitle({ mode: t.mode ?? 'auto', signals: t.signals ?? [], text: t.text ?? '' });
}

/**
 * A slicer end on the wire: epoch seconds for a time (an ISO timestamp without
 * an offset reads as UTC), the number itself otherwise, empty when open.
 */
function slicerEnd(value: number | string | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  const trimmed = value.trim();
  if (trimmed !== '' && Number.isFinite(Number(trimmed))) return trimmed;
  const dated = /^\d{4}-\d\d-\d\d$/.test(trimmed) ? `${trimmed}T00:00:00` : trimmed;
  const iso = /[zZ]$|[+-]\d\d:?\d\d$/.test(dated) ? dated : `${dated}Z`;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? String(Math.round(ms / 1000)) : '';
}

/** The `filters` param for a spec's slicers, or '' when it has none. */
export function serializeSlicers(slicers: NonNullable<PlotSpec['slicers']>): string {
  return slicers
    .filter((s) => s.column)
    .map((s) => `${s.column}|~|${slicerEnd(s.min)}~${slicerEnd(s.max)}`)
    .join(';;');
}

/** Build the plot query string for a spec (mirrors `appendPlotConfig`). */
export function specToQuery(spec: PlotSpec): string {
  const resolvedStart = spec.start_date || startDate.value;
  const resolvedEnd = spec.end_date || endDate.value;
  assertValidProposalDates(resolvedStart, resolvedEnd);
  const p = new URLSearchParams();
  p.set('tab', 'plot');
  p.set('ds', spec.source ?? 'historical');
  p.set('ship', spec.ship_id);
  // Comparison ships ride the same `ships` param the sidebar and share links
  // use, so a proposed comparison restores exactly like a hand-built one.
  const compare = (spec.compare_ship_ids ?? []).filter((s) => s && s !== spec.ship_id);
  if (compare.length) {
    p.set('ships', compare.join(','));
    if (spec.view_mode === 'grid') p.set('vm', 'grid');
  }
  // Dates: a Live chart's rolling window, so it keeps up; else the spec's
  // dates; otherwise the current selection so the plot has a window.
  if (spec.source === 'live' && spec.live_window) {
    p.set('preset', spec.live_window);
  } else {
    p.set('start', resolvedStart);
    p.set('end', resolvedEnd);
  }

  // Period comparison rides `pc`, exactly as a hand-built one does, so Apply
  // restores it through the same compatibility checks a shared link gets.
  // Ids are the ones the editor would allocate: B, C, D in order.
  const periods = spec.compare_periods ?? [];
  if (periods.length && !compare.length && (spec.plot_type === 'line' || spec.plot_type === 'scatter')) {
    periods.forEach((period) => assertValidProposalDates(period.start_date, period.end_date));
    const utc = (value: string) => normalizeUtcDatetime(toDatetimeLocal(value)) ?? value;
    p.set(
      'pc',
      serializePeriodComparison({
        version: PERIOD_COMPARISON_VERSION,
        referenceLabel: spec.reference_label?.trim() || 'Reference',
        additional: periods.map((period, i) => ({
          id: `period-${String.fromCharCode(98 + i)}`,
          label: period.label ?? '',
          start: utc(period.start_date),
          end: utc(period.end_date),
        })),
        timeAxis: spec.period_time_axis === 'utc' ? 'utc' : 'elapsed',
        linkNavigation: true,
      }),
    );
    // Period A is compared in UTC like the rest; an offset date would otherwise
    // land in the date inputs as written rather than as the instant it means.
    p.set('start', utc(resolvedStart));
    p.set('end', utc(resolvedEnd));
  }

  if (spec.plot_type && spec.plot_type !== 'line') p.set('pt', spec.plot_type);
  if (spec.x_column && spec.x_column !== 'timestampNDC') p.set('x', spec.x_column);
  if (spec.y_left?.length) p.set('yl', spec.y_left.join(','));
  if (spec.y_right?.length) p.set('yr', spec.y_right.join(','));
  const yt = specAxisTitle(spec.y_title);
  if (yt) p.set('yt', yt);
  const yrt = specAxisTitle(spec.y_right_title);
  if (yrt) p.set('yrt', yrt);
  if (spec.hist_columns?.length) p.set('hc', spec.hist_columns.join(','));
  if (spec.hist_bins) p.set('bins', String(spec.hist_bins));
  if (spec.plot_type === 'heatmap') {
    if (spec.y_column) p.set('hy', spec.y_column);
    if (spec.z_column) {
      p.set('hz', spec.z_column);
      if (spec.z_agg && spec.z_agg !== 'avg') p.set('hagg', spec.z_agg);
    }
    if (spec.bins) p.set('hb', String(spec.bins));
  }
  if (spec.plot_type === 'correlation') {
    if (spec.corr_columns?.length) p.set('cc', spec.corr_columns.join(','));
    if (spec.corr_method && spec.corr_method !== 'pearson') p.set('cmeth', spec.corr_method);
    if (spec.corr_order && spec.corr_order !== 'given') p.set('cord', spec.corr_order);
  }
  if (spec.plot_type === 'number') serializeNumber({
    ...seededPlotState(),
    number_mode: spec.number_mode ?? 'signal', number_column: spec.number_column ?? '',
    number_agg: spec.number_agg ?? 'latest', number_expression: spec.number_expression ?? '',
    number_label: spec.number_label ?? '', number_unit: spec.number_unit ?? '',
    number_decimals: spec.number_decimals ?? 2,
  }, p);
  if (spec.plot_type === 'gauge') {
    serializeGauge({
      gauge_dials: (spec.gauge_dials ?? []).map(dialFromSpec),
      gauge_layout: spec.gauge_layout ?? 'ship',
    }, p);
  }
  if (spec.plot_type === 'sankey') {
    serializeSankey({
      sankey_nodes: nodesFromSpec(spec.sankey_nodes ?? []),
      sankey_display: {
        unit: spec.sankey_unit ?? SANKEY_DISPLAY_DEFAULT.unit,
        decimals: spec.sankey_decimals ?? SANKEY_DISPLAY_DEFAULT.decimals,
        motion: spec.sankey_motion ?? SANKEY_DISPLAY_DEFAULT.motion,
      },
    }, p);
  }
  if (spec.plot_type === 'pie') {
    if (spec.pie_mode === 'bands') {
      p.set('pmo', 'bands');
      if (spec.pie_band_column) p.set('pbc', spec.pie_band_column);
      if (spec.pie_band_edges?.length) p.set('pbe', spec.pie_band_edges.join(','));
    } else {
      if (spec.pie_columns?.length) p.set('pcol', spec.pie_columns.join(','));
      if (spec.pie_agg && spec.pie_agg !== 'avg') p.set('pagg', spec.pie_agg);
    }
  }
  if (spec.plot_type === 'box') {
    if (spec.box_mode === 'groups') {
      p.set('bmo', 'groups');
      if (spec.box_column) p.set('bcol', spec.box_column);
      if (spec.box_group_column) p.set('bgrp', spec.box_group_column);
    } else if (spec.box_columns?.length) {
      p.set('bcols', spec.box_columns.join(','));
    }
  }
  if (spec.plot_type === 'table') {
    serializeTable({
      table_mode: spec.table_mode ?? 'summary',
      table_page: 0,
      table_page_size: TABLE_PAGE_SIZE_DEFAULT,
      table_columns: spec.table_columns ?? [],
      table_groups: spec.table_groups ?? [],
      table_measures: spec.table_measures ?? [],
      table_sort: '',
    }, p);
  }
  if (spec.plot_type === 'map') {
    if (spec.latitude_column) p.set('lat', spec.latitude_column);
    if (spec.longitude_column) p.set('lon', spec.longitude_column);
  }
  if ((spec.plot_type === 'scatter' || spec.plot_type === 'map') && spec.color_by) {
    p.set('cb', spec.color_by);
  }
  // Slicers ride `filters` as `col|~|min~max`, exactly as a hand-made slicer
  // is shared, so Apply reopens them as slicers under the chart.
  const slicers = serializeSlicers(spec.slicers ?? []);
  if (slicers) p.set('filters', slicers);
  if (spec.filter_expr) {
    p.set('fexpr', spec.filter_expr);
    p.set('fmode', 'and');
  }

  // Derived signals (expr mode): `name|EXPR|<expression>` joined by ',,' - the
  // same wire format parseDerivedParam reads (see url-sync appendDataShaping).
  if (spec.derived?.length) {
    p.set('d', spec.derived.map((d) => `${d.name}|EXPR|${d.expr}`).join(',,'));
  }

  // Fits (line/scatter): `series|kind|p=v;…|hex` joined by ',,' (see parseFitsParam).
  if ((spec.plot_type === 'line' || spec.plot_type === 'scatter') && spec.fits?.length) {
    p.set(
      'f',
      spec.fits
        .map((fit) => {
          const params = Object.entries(fit.params ?? {})
            .map(([k, v]) => `${k}=${v}`)
            .join(';');
          const parts = [fit.series, fit.kind, params];
          if (fit.color) parts.push(fit.color.replace('#', ''));
          return parts.join('|');
        })
        .join(',,'),
    );
  }

  // Per-signal colors: `signal:hex(no #)` joined by ',' (see parsePlotState `colors`).
  const colors = spec.custom_colors ?? {};
  const colorKeys = Object.keys(colors);
  if (colorKeys.length) {
    p.set(
      'colors',
      colorKeys.map((c) => `${c}:${(colors[c] ?? '').replace('#', '')}`).join(','),
    );
  }
  return p.toString();
}

/** Clear every state surface not represented by PlotSpec before URL restore. */
export function resetPlotProposalState(spec: PlotSpec): void {
  activeDataSource.set(spec.source ?? 'historical');
  // The person's appearance preferences survive an applied proposal; only the
  // chart's own configuration is replaced.
  plotState.set(seededPlotState());
  filters.set([]);
  filterLogic.set('AND');
  filterExpr.set('');
  filterExprError.set(null);
  filterMode.set('and');
  outliers.set({ ...DEFAULT_OUTLIERS, columns: [] });
  derivedSignals.set([]);
}

/**
 * Show the result of an Apply.
 *
 * A proposal can be applied from anywhere now that the assistant is a drawer as
 * well as a section - from a dashboard, from the catalog, from its own full
 * view. Wherever it came from, the answer to Apply is a chart in Explore, so
 * the application goes there. Plotting keeps the conversation beside the
 * result, preserving an explicitly minimized or closed drawer.
 */
export function revealInExplore(tab: 'plot' | 'export'): void {
  if (tab === 'export') closeAssistantDrawer();
  else if (activeSection.value === 'assistant') assistantDrawerOpen.set(true);
  activeSection.set('explore');
  document.body.dataset.section = 'explore';
  activeTab.set(tab);
  document.body.dataset.activeTab = tab;
  syncAssistantSurface();
}

export async function applyProposedPlot(spec: PlotSpec): Promise<void> {
  // Build and validate before mutating any application state. This protects old
  // retained cards containing values such as `-6m` from poisoning date atoms.
  const query = specToQuery(spec);
  await ensureProposalSource(spec.source ?? 'historical');
  resetPlotProposalState(spec);
  revealInExplore('plot');

  await restoreFromUrl('?' + query);
  requestAnimationFrame(() => resizePlot());
  await doPlot();
}
